import useAuth from "@/lib/auth/useAuth";
import useConfirm from "@/lib/confirm/useConfirm";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import {
    SUMMARY_OPERATION_IMAGE_LAYER,
    SUMMARY_OPERATIONS_SOURCE,
} from "@/lib/map/layers";

import useMap from "@/lib/map/useMap";
import useRequest from "@/lib/request/useRequest";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { roundTo } from "@/lib/utils/numbers";
import { ApplicationOperation, ListApplications } from "@/types/operations";
import {
    faTrash,
    faMap,
    faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Chip } from "@heroui/react";
import { UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ImageSource } from "mapbox-gl";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import TableV2 from "@/components/table/TableV2";
import { ProcessingStatus } from "@/types/enums";

function SummaryTable({
    operationsQuery,
}: {
    operationsQuery: UseQueryResult<ListApplications>;
}) {
    const { formatUnit, formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("operations");
    const { isConsultantOrSentinel } = useAuth();
    const { fieldBbox, fieldId } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const [selectedOperationId, setSelectedOperationId] = useState<
        number | undefined
    >();
    const navigate = useNavigate();

    const imageQuery = useGetRequest({
        url: `application_operations/${selectedOperationId}/map_display_image/`,
        responseType: "blob",
        enabled: !!selectedOperationId,
    });

    const [sortDescriptor, setSortDescriptor] = useState<SortingState>([
        {
            id: "start_date",
            desc: false,
        },
    ]);

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox = selectedOperationId
            ? (invertedTurfBboxToFourCornerCoordinates(
                  operationsQuery.data?.operations.find(
                      (op) => op.id === selectedOperationId,
                  )?.bounds,
              ) as Coordinates)
            : undefined;
        if (imageUrl) {
            const imSource = map.getSource<ImageSource>(
                SUMMARY_OPERATIONS_SOURCE,
            );
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(SUMMARY_OPERATIONS_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer(SUMMARY_OPERATION_IMAGE_LAYER);
            }
        } else {
            removeLayersIfExists(map, [SUMMARY_OPERATION_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [SUMMARY_OPERATIONS_SOURCE]);
        }
    }, [
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        operationsQuery.data,
        selectedOperationId,
    ]);

    const mutate = useMutateRequest({
        method: "delete",
        errorMessage: t("error.failed_to_delete_operation"),
        successMessage: t("success.operation_deleted_successfully"),
    });

    const confirm = useConfirm();

    const handleDelete = useCallback(
        async (operationId: number) => {
            confirm.showConfirm({
                title: "Delete Operation",
                description: "Are you sure you want to delete this operation?",
                color: "danger",
                confirmText: "Delete",
                onConfirm: async () => {
                    mutate.mutate(
                        {
                            url: `application_operations/${operationId}/`,
                        },
                        {
                            onSuccess: () => {
                                operationsQuery.refetch();
                            },
                        },
                    );
                },
            });
        },
        [confirm, mutate, operationsQuery],
    );

    const columns: ColumnDef<ApplicationOperation>[] = useMemo(() => {
        return [
            {
                accessorKey: "start_date",
                header: t("date"),
                cell: ({ row }) => {
                    return operationsQuery.data?.operations.find(
                        (op) =>
                            op.id !== row.original.id &&
                            op.start_date === row.original.start_date,
                    ) ? (
                        <Chip
                            color="warning"
                            radius="sm"
                            size="sm"
                            variant="flat"
                        >
                            {formatDateMMDDYYYY(row.original.start_date)}
                        </Chip>
                    ) : (
                        formatDateMMDDYYYY(row.original.start_date)
                    );
                },
                size: 100,
            },
            {
                accessorKey: "product",
                header: t("product"),
                cell: ({ row }) => {
                    return row.original.application_products.length === 0 ? (
                        <Chip
                            color="warning"
                            radius="sm"
                            size="sm"
                            variant="flat"
                        >
                            No product
                        </Chip>
                    ) : (
                        (row.original.application_products[0]?.product?.name ??
                            "--")
                    );
                },
                size: 100,
            },
            {
                accessorKey: "method",
                header: t("method"),
                cell: ({ row }) => row.original.method,
                size: 100,
            },
            {
                accessorKey: "average_N",
                header: `${t("nitrogenRate")} (${formatUnit(
                    "lb_ac",
                    "kg_ha",
                )})`,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row justify-center">
                            {!!row.original.file_processing_status &&
                            row.original.file_processing_status !==
                                ProcessingStatus.Completed ? (
                                <FontAwesomeIcon
                                    icon={faHourglassHalf}
                                    className="text-gray-500"
                                />
                            ) : row.original.average_N ? (
                                roundTo(row.original.average_N, 1)
                            ) : (
                                "--"
                            )}
                        </div>
                    );
                },
                size: 100,
            },
            {
                accessorKey: "actions",
                header: t("actions"),
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row gap-1 items-center">
                            <Button
                                isIconOnly
                                color="primary"
                                variant={
                                    selectedOperationId === row.original.id
                                        ? "solid"
                                        : "bordered"
                                }
                                className="tiny-icon-button"
                                onPress={() => {
                                    setSelectedOperationId(
                                        selectedOperationId === row.original.id
                                            ? undefined
                                            : row.original.id,
                                    );
                                }}
                                aria-label="View Operation"
                            >
                                <FontAwesomeIcon icon={faMap} />
                            </Button>
                            {isConsultantOrSentinel && (
                                <Button
                                    isIconOnly
                                    color="danger"
                                    className="tiny-icon-button"
                                    onPress={() =>
                                        handleDelete(row.original.id)
                                    }
                                    aria-label="Delete Operation"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            )}
                        </div>
                    );
                },
                size: 100,
            },
        ];
    }, [
        t,
        formatDateMMDDYYYY,
        formatUnit,
        selectedOperationId,
        isConsultantOrSentinel,
        handleDelete,
        operationsQuery.data?.operations,
    ]);

    return (
        <div>
            <TableV2
                columns={columns}
                data={operationsQuery.data?.operations || []}
                sorting={sortDescriptor}
                setSorting={setSortDescriptor}
                isLoading={operationsQuery.isLoading}
                onRowClick={(row) => {
                    navigate({
                        to: `/dashboard/fields/${fieldId}/operations/application/${row.id}`,
                    });
                }}
            />
        </div>
    );
}

export default SummaryTable;
